import { type FC, useState } from "react"
import { useNavigate } from "react-router"
import styled from "styled-components"

import {
	type VideoCaptionLanguageCode,
	videoCaptionLanguageCodes,
	videoCaptionLanguages,
} from "@forento/core/models/video"
import Tooltip from "@forento/ui/components/Tooltip"
import { useFile } from "@forento/ui/hooks/useFile"
import { toggleStateArray } from "@forento/ui/utilities/array"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import FileChooser from "~/components/FileChooser"
import { SettingsIcon, SubtitlesIcon } from "~/components/Icon"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import Layout, { HelpCenter, HelpCenterIcon, PageBreadcrumb, PageHeader } from "~/components/Layout"
import ManageTagsModal from "~/components/ManageTagsModal"
import { DefaultModal, ModalButtons, ModalInputs, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ProductPricing from "~/components/ProductPricing"
import RichContentEditor from "~/components/RichContentEditor"
import SelectTags from "~/components/SelectTags"
import { useAlert } from "~/contexts/AlertContext"
import usePrice from "~/hooks/usePrice"
import { arrayBufferToBase64 } from "~/utilities/buffer"
import { exportRichContent, type RichContentValue } from "~/utilities/richContent"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const CreateVideoPage: FC = () => {
	const navigate = useNavigate()
	const alert = useAlert()

	const [title, setTitle] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const longDescription = useRichTextArea({ label: "Description" })
	const [presentation, setPresentation] = useState<RichContentValue | null>(null)
	const [isSignInRequired, setSignInRequired] = useState(false)
	const { price, priceValue, setPrice } = usePrice()
	const [isManualOnly, setManualOnly] = useState(false)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(false)
	const [membershipIds, setMembershipIds] = useState<number[]>([])
	const [isPaused, setPaused] = useState(false)
	const [selectedTags, setSelectedTags] = useState<number[]>([])
	const [captions, setCaptions] = useState<{ language: VideoCaptionLanguageCode; file: File }[]>([])
	const [newCaptionLanguage, setNewCaptionLanguage] = useState<VideoCaptionLanguageCode>("en")
	const [newCaptionFile, setNewCaptionFile] = useState<File | null>(null)

	const [isManageTagsModalOpen, setManageTagsModalOpen] = useState(false)
	const [isSubmitting, setSubmitting] = useState(false)

	const thumbnail = useFile(0, null)

	const tags = query.video.listTags.useQuery()
	const memberships = query.membership.list.useQuery()

	const handleSubmit = async () => {
		if (title.trim().length === 0) return

		setSubmitting(true)
		try {
			const { video, captionErrors } = await trpc.video.create.mutate({
				title,
				shortDescription,
				longDescription: await longDescription.exportEditorState(),
				presentation: exportRichContent(presentation),
				thumbnailDataUrl: await thumbnail.exportToDataUrl(),
				isSignInRequired,
				price: priceValue,
				isManualOnly,
				isBokaderoMemberRequired,
				membershipIds,
				isPaused,
				tags: selectedTags,
				captions: await Promise.all(
					captions.map(async caption => ({
						language: caption.language,
						base64: arrayBufferToBase64(await caption.file.arrayBuffer()),
					})),
				),
			})
			if (captionErrors.length > 0) {
				await alert.show(
					"Invalid captions",
					`Your uploaded captions are invalid. You can edit the video and upload new captions later.\n\n${captionErrors.join("\n\n")}`,
				)
			}
			navigate(routes.video.upload(video.id))
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
	}

	return (
		<Layout>
			<DefaultModal onClose={() => setNewCaptionFile(null)} isOpen={Boolean(newCaptionFile)}>
				<ModalTitle>Add captions</ModalTitle>
				<ModalInputs>
					<Dropdown
						items={videoCaptionLanguageCodes.map(x => ({ id: x, title: videoCaptionLanguages[x] }))}
						selectedItemId={newCaptionLanguage}
						onChange={value =>
							setNewCaptionLanguage(videoCaptionLanguageCodes.find(x => x === value) ?? "en")
						}
					/>
				</ModalInputs>
				<ModalButtons>
					<Button variant="secondary" onClick={() => setNewCaptionFile(null)}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={() => {
							if (!newCaptionFile) return
							setCaptions(current => [...current, { language: newCaptionLanguage, file: newCaptionFile }])
							setNewCaptionFile(null)
							setNewCaptionLanguage("en")
						}}
					>
						Add captions
					</Button>
				</ModalButtons>
			</DefaultModal>
			<PageHeader>
				<PageBreadcrumb title="Upload" path={[{ title: "Videos", link: routes.video.index() }]} />
				<HelpCenter href="https://forento.link/create-video" target="_blank" rel="noreferrer noopener">
					<HelpCenterIcon />
					Learn more about videos
				</HelpCenter>
			</PageHeader>
			{memberships.error || tags.error ? (
				<p>Failed to load data.</p>
			) : memberships.data === undefined || tags.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<Container>
					<ManageTagsModal
						type="video"
						isOpen={isManageTagsModalOpen}
						tags={tags.data}
						onClose={() => setManageTagsModalOpen(false)}
						onChange={tags.refetch}
					/>

					<Section>
						<Title>Basic information</Title>
						<InputField label="Title" value={title} onChange={setTitle} />
						<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
						{longDescription.element}
						<div>
							<ImageUploader
								disableSvg
								label="Thumbnail"
								recommendedSize="thumbnail"
								{...thumbnail.imageUploader}
							/>
							<ThumbnailDescription>
								If you don't upload a thumbnail it will automatically choose one from on the video.
							</ThumbnailDescription>
						</div>
					</Section>

					<Section>
						<Title>Pricing</Title>
						<ProductPricing
							type="video"
							isSignInRequired={isSignInRequired}
							setSignInRequired={setSignInRequired}
							price={price}
							setPrice={setPrice}
							isManualOnly={isManualOnly}
							setManualOnly={setManualOnly}
							isBokaderoMemberRequired={isBokaderoMemberRequired}
							setBokaderoMemberRequired={setBokaderoMemberRequired}
							membershipIds={membershipIds}
							setMembershipIds={setMembershipIds}
							isPaused={isPaused}
							setPaused={setPaused}
						/>
					</Section>

					<Section>
						<Header>
							<Title>Tags</Title>
							<Tooltip tooltip="Manage tags">
								<button
									type="button"
									onClick={() => setManageTagsModalOpen(true)}
									className="cursor-pointer"
								>
									<SettingsIcon className="size-6" />
								</button>
							</Tooltip>
						</Header>
						<Description>
							Tags help students find your video when they search for specific topics.
						</Description>
						<SelectTags
							tags={tags.data}
							selectedTags={selectedTags}
							onChange={tagId => toggleStateArray(setSelectedTags, tagId)}
						/>
					</Section>

					<Section>
						<Title>Captions</Title>
						{captions.length > 0 && (
							<Captions>
								{captions.map(caption => (
									<Caption key={caption.language}>
										<CaptionIcon />
										{videoCaptionLanguages[caption.language]}
									</Caption>
								))}
							</Captions>
						)}
						<UploadCaptions>
							<FileChooser type="video-captions" value={newCaptionFile} onChange={setNewCaptionFile} />
						</UploadCaptions>
					</Section>

					<Section>
						<Title>Presentation</Title>
						<RichContentEditor
							initialValue={null}
							placeholder="Enter video presentation here..."
							setValue={setPresentation}
						/>
					</Section>

					<Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} className="self-end">
						Save
					</Button>
				</Container>
			)}
		</Layout>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
	padding-bottom: 128px;
`

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const Title = styled.h2`
	font-weight: 600;
	font-size: 24px;
	color: black;
`

const Description = styled.p`
	color: black;
`

const ThumbnailDescription = styled.p`
	color: black;
	margin-top: 8px;
`

const Captions = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
`

const Caption = styled.div`
	padding: 8px 16px;
	border-radius: 36px;
	line-height: 1;
	color: white;
	background-color: ${props => props.theme.primaryColor};
	display: flex;
	align-items: center;
	gap: 8px;
`

const CaptionIcon = styled(SubtitlesIcon)`
	width: 20px;
	height: 20px;
`

const UploadCaptions = styled.div`
	max-width: 300px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export default CreateVideoPage
